import { Component, inject, OnInit } from '@angular/core';
import { ItemService } from './item.service';
import { SpinnerOverlayService } from '../../../Components/spinner/spinner-overlay.service';
import { NgClass } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MessageService } from '../../../shared/message.service';
import { ITEMS_PER_PAGE } from '../../../core/config/pagination.constants';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ItemDeleteComponent } from '../item-delete/item-delete.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomInput } from '../../../Components/input/custom-input';
import { Button } from '../../../Components/button/button';
import { IItem } from './item.model';
import { ItemMassiveLoadResponse } from './item.service';
import { ItemAddRelations } from '../item-add-relations/item-add-relations';
import {ItemUpdateComponent} from '../item-update/item-update.component';
import * as XLSX from 'xlsx';
import { MassiveLoadError } from '../../../shared/model/massive-load.model';
import { MassiveLoadErrorsComponent } from '../../../shared/massive-load-errors/massive-load-errors.component';
import { MassiveLoadTemplateDialog, TemplateDownloadScope } from '../../../shared/massive-load-template-dialog/massive-load-template-dialog';

@Component({
  selector: 'app-item',
  imports: [
    NgClass,
    TableModule,
    ReactiveFormsModule,
    FormsModule,
    CustomInput,
    Button,
    MassiveLoadErrorsComponent
  ],
  templateUrl: './item.component.html',
  styleUrl: './item.component.css'
})
export class Item implements OnInit {
  itemList: IItem[] = []

  messageService = inject(MessageService);

  totalItems: number = 2
  first = 0;
  rows = ITEMS_PER_PAGE;
  page = 0;
  itemDescription: string | null = null
  lastEvent: any;
  sortField = 'id';
  sortOrder = 1;
  massiveLoadErrors: MassiveLoadError[] = [];
  totalMassiveLoadErrors = 0;
  showMassiveLoadErrors = false;

  constructor(
    private itemService: ItemService,
    private spinner: SpinnerOverlayService,
    private dialog: MatDialog
  ) {
  }

  ngOnInit() {
    this.findAll()
  }

  openAddRelationsModal(item: IItem, itemId: string) {
    const dialogRef = this.dialog.open(ItemAddRelations, {
      width: '800px',
      data: { itemId: itemId, item: item },
      panelClass: 'lexend-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((saved: any) => {
      if (saved) {
        this.findAll(true);
      }
    });
  }

  findAll(reset: boolean = false) {
    this.handleLoading(true)

    if (reset) {
      this.sortField = "id"
      this.sortOrder = 1
    }

    const params: any = {
      page: reset ? 0 : this.page,
      size: this.rows,
      sort: `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`,
      criteria: this.itemDescription
    };

    this.itemService.findByParams(params).subscribe({
      next: (response) => {
        this.itemList = response.body?.item?.itemList ?? []
        this.totalItems = response.body?.item?.totalCount ?? 0
        this.handleLoading(false)
      },
      error: () => {
        this.handleLoading(false)
      }
    })
  }

  handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide()
  }

  getBackgroundClasses(active: boolean) {
    if (active) return 'bg-green-100';
    return 'bg-red-100';
  }

  openActivateItemModal(description: string, id: number, active: boolean = false) {
    const dialogRef = this.dialog.open(ItemDeleteComponent, {

      data: {
        description: description,
        active: active
      },
      panelClass: 'lexend-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.activeOrInactiveItem(id, active)
      }
    });
  }

  activeOrInactiveItem(id: number, active: boolean) {
    this.handleLoading(true)
    this.itemService.activate(active, id).subscribe({
      next: () => {
        this.findAll()
        const message = active ? "activado" : "desactivado"
        this.messageService.showSuccess(`Ítem ${message} con exito`)
        this.handleLoading(false)
      },
      error: () => {
        this.handleLoading(false)
      }
    })
  }

  reset() {
    this.first = 0;
  }

  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }

  loadItems(event: TableLazyLoadEvent) {
    if (JSON.stringify(event) === JSON.stringify(this.lastEvent)) {
      return;
    }
    this.lastEvent = event;

    this.sortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField ?? 'id';
    this.sortOrder = event.sortOrder ?? 1;
    this.page = Math.floor((event.first ?? 0) / (event.rows ?? 10));
    this.rows = event.rows ?? 10;

    this.findAll();
  }

  cleanFilter() {
    this.itemDescription = null;
    this.findAll(true);
  }

  downloadTemplate() {
    if (!this.itemList.length) {
      this.messageService.showError('No hay ítems visibles para generar la plantilla.');
      return;
    }

    const dialogRef = this.dialog.open(MassiveLoadTemplateDialog, {
      width: '32rem',
      panelClass: 'lexend-dialog',
      disableClose: true,
      data: {
        entityLabel: 'ítems',
        filteredCount: this.hasItemFilter() ? this.totalItems : this.itemList.length
      }
    });

    dialogRef.afterClosed().subscribe((scope: TemplateDownloadScope | undefined) => {
      if (scope === 'FILTERED') {
        if (this.hasItemFilter()) {
          this.downloadItemsForTemplate(this.itemDescription, this.totalItems);
        } else {
          this.createTemplate(this.itemList);
        }
      } else if (scope === 'ALL') {
        this.downloadAllItemsForTemplate();
      }
    });
  }

  private hasItemFilter(): boolean {
    return (this.itemDescription?.trim().length ?? 0) > 0;
  }

  private downloadAllItemsForTemplate() {
    this.handleLoading(true);
    this.itemService.findByParams(this.templateParams(null, 1)).subscribe({
      next: response => {
        const total = Number(response.body?.item?.totalCount ?? 0);
        this.downloadItemsForTemplate(null, total);
      },
      error: () => {
        this.handleLoading(false);
        this.messageService.showError('No se pudieron consultar todos los ítems.');
      }
    });
  }

  private downloadItemsForTemplate(criteria: string | null, total: number) {
    if (total <= 0) {
      this.handleLoading(false);
      this.messageService.showError('No hay ítems disponibles para generar la plantilla.');
      return;
    }

    this.handleLoading(true);
    this.itemService.findByParams(this.templateParams(criteria, total)).subscribe({
      next: response => {
        this.handleLoading(false);
        const items = response.body?.item?.itemList ?? [];
        this.createTemplate(items);
      },
      error: () => {
        this.handleLoading(false);
        this.messageService.showError('No se pudieron consultar los ítems para generar la plantilla.');
      }
    });
  }

  private templateParams(criteria: string | null, size: number) {
    return {
      page: 0,
      size,
      sort: `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`,
      criteria
    };
  }

  private createTemplate(items: IItem[]) {
    const headers = [
      'CODIGO',
      'DESCRIPCION',
      'TARIMA',
      '½ TARIMA',
      '¼ TARIMA',
      'MINIMA TARIMA',
      'DIAS ENTREGA',
      'ACTIVO'
    ];

    const rows = items.map(item => ({
      'CODIGO': item.id,
      'DESCRIPCION': item.description ?? '',
      'TARIMA': item.platform ?? 0,
      '½ TARIMA': item.halfPlatform ?? 0,
      '¼ TARIMA': item.quarterPlatform ?? 0,
      'MINIMA TARIMA': item.eighthPlatform ?? 0,
      'DIAS ENTREGA': item.leadTime ?? 0,
      'ACTIVO': item.active ? 'T' : 'F'
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    worksheet['!cols'] = [
      { wch: 24 }, { wch: 40 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 12 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Items');
    XLSX.writeFile(workbook, 'plantilla-items.xlsx');
  }

  onItemFileSelected(event: Event, fileInput: HTMLInputElement) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.messageService.showError('Seleccione un archivo Excel con extensión .xlsx.');
      return;
    }

    this.massiveLoadErrors = [];
    this.totalMassiveLoadErrors = 0;
    this.showMassiveLoadErrors = false;
    this.handleLoading(true);
    this.itemService.massiveLoad(file).subscribe({
      next: response => {
        this.handleLoading(false);
        this.handleMassiveLoadResponse(response.body);
        this.findAll();
      },
      error: error => {
        this.handleLoading(false);
        const response = error?.error as ItemMassiveLoadResponse | undefined;
        const technicalError = response?.technicalError;
        this.messageService.showError(
          technicalError || 'No se pudo procesar la carga masiva de ítems.'
        );
      }
    });
  }

  private handleMassiveLoadResponse(response: ItemMassiveLoadResponse | null) {
    if (!response) {
      this.messageService.showError('El servidor no devolvió un resultado válido.');
      return;
    }

    if (response.technicalError) {
      this.messageService.showError(response.technicalError);
      return;
    }

    const errors = [...(response.errors ?? [])].sort((first, second) =>
      first.rowNumber - second.rowNumber
    );
    this.massiveLoadErrors = errors.map(error => ({
      ...error,
      entityType: 'ITEM' as const
    }));
    this.totalMassiveLoadErrors = response.totalErrors ?? errors.length;
    this.showMassiveLoadErrors = false;

    if (!errors.length) {
      this.messageService.showMassiveLoadSuccess(
        `Carga completada. ${response.updatedRows} ítem(s) actualizado(s).`
      );
      return;
    }

    if (response.updatedRows > 0) {
      this.messageService.showMassiveLoadSuccess(
        `Carga parcial completada. ${response.updatedRows} ítem(s) actualizado(s).`
      );
    }
    this.messageService.showErrorHtml(
      `Se encontraron ${this.totalMassiveLoadErrors} error(es). Presione "Ver errores" para consultar el detalle.`
    );
  }

  openEditModal(id: number | null) {
    const dialogRef = this.dialog.open(ItemUpdateComponent, {
      width: '90%',
      height: 'auto',
      data: id ? {
        id: id
      } : null,
      panelClass: 'lexend-dialog-wide',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.findAll(true)
        this.messageService.showSuccess("Producto actualizado con exito")
      }
    });
  }
}
